import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { SidAPI } from "../../services/SidAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { CreateRoleRequestProps } from "../../types/custom/Permissions"
import type { Role } from "../../types/generated/authentication/Role"
import type { RolePermission } from "../../types/generated/authentication/RolePermission"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { getRoleDisplayName } from "../../utils/rolesHelpers"
import type { MenuOption } from "../Members"
import type { RoleContainerProps } from "./Interfaces"
import { RoleCard } from "./RoleCard"
import { RoleCreationDialog } from "./RoleCreationDialog"

/**
 * Role container holding all the role logic
 * @param {Role} role
 * @param {string} users (optional)
 * @returns {React.ReactElement}
 */
export function RoleContainer(props: Readonly<RoleContainerProps>): React.ReactElement {
  const { role, users } = props

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const { t } = useTranslation("settings", {
    keyPrefix: "accessControl.sections.roles.container",
  })

  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState<boolean>(false)
  const [isViewMode, setIsViewMode] = useState<boolean>(false)
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  const OrganizationUID = useSelector((state: RootState) => state.auth.organizationId) as string
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const deleteRoleMutation = useMutation<AxiosResponse, AxiosError, string>(SidAPI.deleteRole, {
    onSuccess: () => {
      NotificationUtils.toast(t("notifications.deleteRole.success"), {
        severity: "success",
      })

      queryClient.invalidateQueries("roles")
      queryClient.invalidateQueries("change-history")
    },
    onError: () =>
      NotificationUtils.toast(t("notifications.deleteRole.error"), {
        severity: "error",
      }),
  })

  const editRoleMutation = useMutation<AxiosResponse<Role>, AxiosError, CreateRoleRequestProps & { role_uuid: string }>(
    SidAPI.updateRole,
    {
      onSuccess: () => {
        NotificationUtils.toast(t("notifications.editRole.success"), {
          severity: "success",
        })

        queryClient.invalidateQueries("konan-permissions")
        queryClient.invalidateQueries("sid-permissions")
        queryClient.invalidateQueries(["roles", OrganizationUID])
        queryClient.invalidateQueries("change-history")
      },
      onError: () =>
        NotificationUtils.toast(t("notifications.editRole.error"), {
          severity: "error",
        }),
    },
  )

  const updateRoleHandler = async (name: string, description: string, permissions: RolePermission[]): Promise<void> => {
    await editRoleMutation.mutateAsync({
      role_uuid: role.uid,
      name: name,
      description: description,
      permissions: permissions,
    })

    setIsRoleDialogOpen(false)
  }

  const MenuOptions = (): MenuOption[] => {
    const options: MenuOption[] = []

    useIsPermitted("Update roles", permissions.synapse_id, flattenedSidPermissions) &&
      options.push({
        title: t("menu.edit"),
        variant: "base",
        onClick: () => {
          setIsRoleDialogOpen(true)
        },
      })

    useIsPermitted("Delete roles", permissions.synapse_id, flattenedSidPermissions) &&
      options.push({
        title: t("menu.delete"),
        variant: "negative",
        onClick: () => {
          setIsDeletionDialogOpen(true)
        },
      })

    return options
  }

  return (
    <Fragment>
      {isRoleDialogOpen && (
        <RoleCreationDialog
          isOpen={isRoleDialogOpen}
          onClose={() => {
            setIsRoleDialogOpen(false)
            setIsViewMode(false)
          }}
          role={role}
          onAccept={updateRoleHandler}
          isViewMode={isViewMode}
        />
      )}

      <BaseSimpleDialog
        open={isDeletionDialogOpen}
        isLoading={deleteRoleMutation.isLoading}
        name={role.name}
        onAccept={() => {
          deleteRoleMutation.mutateAsync(role.uid)
          setIsDeletionDialogOpen(false)
        }}
        onClose={() => setIsDeletionDialogOpen(false)}
        mode={"role-deletion"}
      />

      <RoleCard
        onTitleClick={() => {
          setIsViewMode(true)
          setIsRoleDialogOpen(true)
        }}
        name={getRoleDisplayName(role.name)}
        date={role.created_at}
        immutable={["OWNER", "USER"].includes(role.name)}
        users={users}
        MenuOptions={MenuOptions()}
      />
    </Fragment>
  )
}
